import React, { useMemo, useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CREATE_LEAVE_REQUEST = gql`
  mutation MobileCreateLeaveRequest($input: LeaveRequestInput!) {
    createLeaveRequest(input: $input) {
      id
      status
      startDate
      endDate
    }
  }
`;

const GET_LEAVE_BALANCE = gql`
  query MobileLeaveBalance($employeeId: String, $year: Int) {
    leaveBalance(employeeId: $employeeId, year: $year) {
      employeeId
      year
      balance
      totalUsed
    }
  }
`;

const LeaveRequest: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leaveType, setLeaveType] = useState('VACATION');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const currentYear = new Date().getFullYear();

  const { data: balanceData } = useQuery(GET_LEAVE_BALANCE, {
    variables: {
      employeeId: user?.id,
      year: currentYear,
    },
    skip: !user?.id,
  });

  const [createLeaveRequest, { loading }] = useMutation(CREATE_LEAVE_REQUEST);

  const leaveTypes = [
    { value: 'VACATION', label: 'Vacation' },
    { value: 'SICK', label: 'Sick Leave' },
    { value: 'PERSONAL', label: 'Personal' },
    { value: 'BEREAVEMENT', label: 'Bereavement' },
    { value: 'JURY_DUTY', label: 'Jury Duty' },
    { value: 'UNPAID', label: 'Unpaid Leave' },
  ];

  const parsedBalance = useMemo(() => {
    const raw = balanceData?.leaveBalance?.balance;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [balanceData]);

  const organizationId = user?.organizationId || user?.organization?.id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user?.id || !organizationId) {
      setError('Missing user organization context. Please sign in again.');
      return;
    }

    try {
      await createLeaveRequest({
        variables: {
          input: {
            organizationId,
            employeeId: user.id,
            leaveType,
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
            reason: reason || null,
          },
        },
      });

      setSuccess('Leave request submitted successfully.');
      setTimeout(() => navigate('/schedule'), 900);
    } catch (submitError: any) {
      setError(submitError?.message || 'Could not submit leave request.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#1a1a2e' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, fontWeight: 700, flexGrow: 1 }}>
            Request Leave
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Leave Details
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                select
                fullWidth
                label="Leave Type"
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                sx={{ mb: 2 }}
              >
                {leaveTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="Reason (Optional)"
                multiline
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ py: 1.5, bgcolor: '#E91E63' }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Submit Request'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, bgcolor: '#E91E6315' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ color: '#E91E63', fontWeight: 600, mb: 1 }}>
              Leave Balance
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Vacation Days</Typography>
              <Typography variant="body2" fontWeight={600}>
                {parsedBalance?.VACATION?.remaining ?? '--'} days remaining
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Sick Days</Typography>
              <Typography variant="body2" fontWeight={600}>
                {parsedBalance?.SICK?.remaining ?? '--'} days remaining
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LeaveRequest;
